import PropertyFormValues from "@/model/PropertyFormValues";
import PropertyRequest from "@/model/PropertyRequest";
import PropertyResponse from "@/model/PropertyResponse";

function trimOrUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function numberOrUndefined(value: string): number | undefined {
  return value.trim() === "" ? undefined : Number(value);
}

function emptyValues(): PropertyFormValues {
  return {
    title: "",
    description: "",
    propertyType: "APARTMENT",
    status: "LISTED",
    bookingMode: "INSTANT",
    street: "",
    postalCode: "",
    city: "",
    country: "",
    latitude: null,
    longitude: null,
    maxGuests: "2",
    bedrooms: "1",
    beds: "1",
    bathrooms: "1",
    surfaceM2: "",
    amenities: [],
    houseRules: "",
    checkInTime: "15:00",
    checkOutTime: "11:00",
    nightlyPriceAmount: "",
    cleaningFeeAmount: "",
    priceCurrency: "EUR",
    minNights: "1",
    placeIds: [],
  };
}

function fromProperty(property: PropertyResponse): PropertyFormValues {
  const toText = (value?: number) =>
    value === undefined || value === null ? "" : String(value);

  return {
    title: property.title ?? "",
    description: property.description ?? "",
    propertyType: property.propertyType ?? "APARTMENT",
    status: property.status ?? "LISTED",
    bookingMode: property.bookingMode ?? "INSTANT",
    street: property.address?.street ?? "",
    postalCode: property.address?.postalCode ?? "",
    city: property.address?.city ?? "",
    country: property.address?.country ?? "",
    latitude: property.address?.latitude ?? null,
    longitude: property.address?.longitude ?? null,
    maxGuests: toText(property.maxGuests),
    bedrooms: toText(property.bedrooms),
    beds: toText(property.beds),
    bathrooms: toText(property.bathrooms),
    surfaceM2: toText(property.surfaceM2),
    amenities: property.amenities ?? [],
    houseRules: property.houseRules ?? "",
    checkInTime: property.checkInTime ?? "",
    checkOutTime: property.checkOutTime ?? "",
    nightlyPriceAmount: toText(property.nightlyPriceAmount),
    cleaningFeeAmount: toText(property.cleaningFeeAmount),
    priceCurrency: property.priceCurrency ?? "EUR",
    minNights: toText(property.minNights),
    placeIds: (property.places ?? []).map((place) => place.id),
  };
}

/** Callers must have checked that a location was picked (latitude/longitude not null). */
function toRequest(
  values: PropertyFormValues,
  extra: Pick<PropertyRequest, "companyId" | "imageUrls"> = {}
): PropertyRequest {
  return {
    ...extra,
    title: values.title.trim(),
    description: trimOrUndefined(values.description),
    propertyType: values.propertyType,
    status: values.status,
    bookingMode: values.bookingMode,
    address: {
      street: values.street.trim(),
      postalCode: trimOrUndefined(values.postalCode),
      city: values.city.trim(),
      country: values.country.trim(),
      latitude: values.latitude ?? 0,
      longitude: values.longitude ?? 0,
    },
    maxGuests: Number(values.maxGuests),
    bedrooms: Number(values.bedrooms),
    beds: Number(values.beds),
    bathrooms: Number(values.bathrooms),
    surfaceM2: numberOrUndefined(values.surfaceM2),
    amenities: values.amenities,
    houseRules: trimOrUndefined(values.houseRules),
    checkInTime: trimOrUndefined(values.checkInTime),
    checkOutTime: trimOrUndefined(values.checkOutTime),
    nightlyPriceAmount: Number(values.nightlyPriceAmount),
    cleaningFeeAmount: numberOrUndefined(values.cleaningFeeAmount),
    priceCurrency: trimOrUndefined(values.priceCurrency),
    minNights: numberOrUndefined(values.minNights),
    placeIds: values.placeIds,
  };
}

/** Rebuilds a full-replace request from a loaded property, e.g. to change only its photos. */
function requestFromProperty(
  property: PropertyResponse,
  imageUrls: string[]
): PropertyRequest {
  return toRequest(fromProperty(property), { imageUrls });
}

const propertyForm = {
  emptyValues,
  fromProperty,
  toRequest,
  requestFromProperty,
};

export default propertyForm;
